/**
 * Every URL this console serves, and every URL it used to serve.
 *
 * One table, so a route and the redirect pointing at it cannot drift apart —
 * a test asserts every redirect target and every nav tab resolves to something
 * in ROUTES.
 *
 * ORDER MATTERS in the tenant block. `/tenants/access` and `/tenants/evidence`
 * must be declared BEFORE `/tenants/:ref`, or the router matches "access" as a
 * business reference and renders the merchant page for a merchant called
 * "access". React Router v6 ranks static segments above dynamic ones so this
 * is belt-and-braces rather than load-bearing — but the failure is silent and
 * looks like a 404 from the API, so the order is pinned by a test too.
 */
export interface RouteDef {
  path: string;
  /** Which page component renders it — resolved in App.tsx. */
  page: string;
}

export const ROUTES: RouteDef[] = [
  { path: "/", page: "FleetPosture" },

  // --- Tenants. Static paths first; see the note above. ---
  { path: "/tenants", page: "Tenants" },
  { path: "/tenants/evidence", page: "FleetEvidence" },
  { path: "/tenants/:ref", page: "Tenant" },
  { path: "/tenants/:ref/usage", page: "TenantUsage" },
  { path: "/tenants/:ref/history", page: "TenantHistory" },
  { path: "/tenants/:ref/users", page: "TenantUsers" },
  { path: "/tenants/:ref/users/:email", page: "TenantUser" },

  // --- Policy ---
  { path: "/policy/tiers", page: "Tiers" },
  { path: "/policy/access", page: "Access" },
  { path: "/policy/caps/:tier", page: "CapsTier" },
  // Labeeb's half of the same drill-in. There is no GET /admin/tiers/{id}
  // — the list is the only read — so this page selects its row out of
  // GET /admin/tiers and PATCHes /admin/tiers/{tier_id}.
  { path: "/policy/tiers/:tier", page: "LabeebTier" },
  { path: "/policy/flags", page: "Flags" },

  // --- Governance ---
  { path: "/governance/permissions", page: "Permissions" },
  { path: "/governance/audit", page: "Audit" },
];

/**
 * Old URL → new URL. Nothing anyone bookmarked or pasted into a ticket breaks.
 *
 * `:param` in a target is filled from the matched route's params, so a
 * reference needing URL-encoding survives the hop. The previous
 * implementation read `window.location.pathname` and split on "/", which
 * mangled exactly those.
 *
 * Two generations are listed: the pre-`/mcp` routes (already redirects before
 * this change) and the `/mcp/*` ones this restructure replaces. The oldest
 * therefore takes two hops, which is fine — a redirect chain costs nothing and
 * collapsing it would mean remembering two mappings instead of one.
 */
export const REDIRECTS: { from: string; to: string }[] = [
  // Absorbed into the page that already summarised them. /policy/caps read
  // the same endpoint as the tier summary and wrote nothing; the EDITOR at
  // /policy/caps/:tier is untouched.
  // Blocking is fleet work — see the number, act on the list — so it moved
  // to Policy. Per-merchant blocking still lives on /tenants/{ref}.
  { from: "/tenants/access", to: "/policy/access" },
  // Beta grants was per-tenant work at fleet level: beta_agents exists only on
  // PATCH /admin/tenant/{id}, and the merchant record now edits it with the
  // rest of that merchant's context around it.
  { from: "/policy/beta", to: "/tenants" },
  // Two unbuilt Labeeb switches, now sections in Labeeb's Settings block.
  { from: "/policy/hitl", to: "/policy/flags" },
  { from: "/policy/welcome", to: "/policy/flags" },
  { from: "/policy/caps", to: "/policy/tiers" },
  // Same question as the flags beside it, so it became a block on that page.
  { from: "/policy/config", to: "/policy/flags" },
  // AI apps is a SECTION inside MCP's Settings block, not a tab of its own:
  // "which apps may connect" is a setting, and Policy's tab strip is for
  // what a change APPLIES TO, not for each knob. The URL keeps working so a
  // link already pasted into a thread still lands somewhere right.
  { from: "/policy/apps", to: "/policy/flags" },
  // Pre-/mcp, kept from the previous restructure.
  { from: "/merchants", to: "/tenants" },
  { from: "/merchants/:ref", to: "/tenants/:ref" },

  // The /mcp/* generation.
  { from: "/mcp", to: "/" },
  { from: "/mcp/merchants", to: "/tenants" },
  { from: "/mcp/merchants/:ref", to: "/tenants/:ref" },
  { from: "/mcp/merchants/:ref/usage", to: "/tenants/:ref/usage" },
  { from: "/mcp/merchants/:ref/history", to: "/tenants/:ref/history" },
  { from: "/mcp/merchants/:ref/users", to: "/tenants/:ref/users" },
  { from: "/mcp/merchants/:ref/users/:email", to: "/tenants/:ref/users/:email" },
  { from: "/mcp/blocks", to: "/policy/access" },
  { from: "/mcp/quotas", to: "/policy/tiers" },
  { from: "/mcp/quotas/:tier", to: "/policy/caps/:tier" },
  { from: "/mcp/catalog", to: "/policy/tiers" },
  { from: "/mcp/settings", to: "/policy/flags" },
  { from: "/status", to: "/governance/permissions" },

  // Folded into the design's own surfaces, so the console's nav is exactly the
  // eight tabs it now carries. The content did not go away — it moved:
  //   the tool catalog  -> a panel under Tool caps
  //   MCP settings      -> Feature flags, beside the Labeeb flags it belongs with
  //   product health    -> Permission map
  //   your access       -> Permission map, which already showed it
  { from: "/policy/catalog", to: "/policy/tiers" },
  { from: "/governance/settings", to: "/policy/flags" },
  { from: "/governance/health", to: "/governance/permissions" },
  { from: "/me", to: "/governance/permissions" },
];
